import React, { Fragment, useEffect, useMemo, useState } from "react";
import { Row, Col, Pagination, Input, Button, DatePicker, Radio, Tooltip } from "antd";
import moment from "moment";
import { SortDescendingOutlined, SortAscendingOutlined } from "@ant-design/icons";
import Image from "helpers/Image";
import { useAttendenceList } from "hooks/reports/reports";
import { usePostDownloadCsv } from "hooks/csv/csv";
import { attendance_type, absentee_type, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from "helpers/commonFunctions";
import { exportCsvToXlsx } from "helpers/exportXlsx";
import "../style.css";

/**
 * Daily attendance -- present and absent in one screen.
 *
 * These were two separate pages ("Today's Attendance" and "Today's Absentees")
 * hitting the same endpoint with a different type flag, duplicating the date
 * picker, search, sorting, paging and export. They are now one screen with a
 * toggle, which also fixes the naming: both always accepted any date, so
 * "Today's" was wrong on each of them.
 */

/**
 * Columns differ by mode: an absent employee has no in/out time to show, so
 * their date & time, location and site are where they were last seen.
 *
 * `key` doubles as the sort field and must be in the API's whitelist
 * (AttendanceFilterDtoV1) -- the absent "Last Seen" column once sorted on
 * `ecl.updated_at`, which the API rejects.
 */
const COLUMNS = {
  [attendance_type]: [
    { key: "e.name", label: "Employee Name", field: "employeeName" },
    { key: "s.site_name", label: "Site", field: "siteName" },
    { key: "ts.card_id", label: "Card ID", field: "employeeCode" },
    { key: "efl.first_in", label: "First In", field: "firstIn", time: true, center: true },
    { key: "efl.last_out", label: "Last Out", field: "lastOut", time: true, center: true },
    { key: "ecl.read_time", label: "Last Seen", field: "lastKnownDateAndTime", time: true, center: true },
    { key: "l.location_name", label: "Location", field: "location" },
  ],
  [absentee_type]: [
    { key: "ts.card_id", label: "Card ID", field: "employeeCode" },
    { key: "e.name", label: "Employee Name", field: "employeeName" },
    // An absent employee was last seen on some other day, so keep the date here.
    { key: "ecl.read_time", label: "Last Seen Date & Time", field: "lastKnownDateAndTime", time: true, withDate: true },
    { key: "l.location_name", label: "Location", field: "location" },
    { key: "s.site_name", label: "Site Name", field: "siteName" },
  ],
};

const MODE_META = {
  [attendance_type]: { title: "Present", file: "Attendance_Report", sheet: "Attendance" },
  [absentee_type]: { title: "Absent", file: "Absentee_Report", sheet: "Absentees" },
};

const DailyAttendance = () => {
  const [mode, setMode] = useState(attendance_type);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchKey, setSearchKey] = useState("");
  const [filterDate, setFilterDate] = useState(moment());
  const [sortField, setSortField] = useState("");
  const [sortMethod, setSortMethod] = useState("desc");
  const [rows, setRows] = useState([]);

  const { mutate: fetchList, data: listRes, isLoading } = useAttendenceList();
  const { mutate: downloadCsv, isLoading: exportLoading } = usePostDownloadCsv("attendanceCsv/v1");

  const columns = COLUMNS[mode];
  const meta = MODE_META[mode];

  /** One filter object, so the table and the export can never disagree. */
  const filters = useMemo(
    () => ({
      date: filterDate ? moment(filterDate).format() : moment().format(),
      type: mode,
      searchKey,
      sortField,
      sortMethod,
      pageNumber,
      pageSize,
    }),
    [filterDate, mode, searchKey, sortField, sortMethod, pageNumber, pageSize]
  );

  useEffect(() => {
    fetchList(JSON.stringify(filters), {
      onSuccess: (response) => setRows(response?.data?.content || []),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const onSort = (key) => {
    setSortMethod((prev) => (sortField === key && prev === "asc" ? "desc" : "asc"));
    setSortField(key);
    setPageNumber(0);
  };

  const onModeChange = (event) => {
    setMode(event.target.value);
    // Sort keys are not shared between the two column sets, so reset rather
    // than send a field the other query does not recognise.
    setSortField("");
    setSortMethod("desc");
    setPageNumber(0);
    setRows([]);
  };

  const onExport = () => {
    const { pageNumber: _p, pageSize: _s, ...exportFilters } = filters;
    downloadCsv(JSON.stringify(exportFilters), {
      onSuccess: (data) => {
        if (data) exportCsvToXlsx(data, meta.file, meta.sheet);
      },
    });
  };

  const total = listRes?.data?.totalElements || 0;
  const offset = listRes?.data?.pageable?.offset || 0;
  const shown = listRes?.data?.numberOfElements || 0;

  const renderCell = (row, column) => {
    const value = row?.[column.field];
    if (!value) return "-";
    if (column.time) {
      // Site-local wall-clock strings, e.g. "2026-08-30 12:53:54.267"; drop the milliseconds.
      const parsed = moment(value, ["DD-MM-YYYY hh:mm ss", "YYYY-MM-DD HH:mm:ss.SSS", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601]);
      if (!parsed.isValid()) return value;
      return (
        <span title={parsed.format("DD MMM YYYY HH:mm:ss")}>
          {parsed.format(column.withDate ? "DD-MM-YYYY HH:mm:ss" : "HH:mm:ss")}
        </span>
      );
    }
    return value;
  };

  return (
    <Fragment>
      <Row className="common-heads" gutter={[12, 12]} align="middle">
        <Col xs={24} md={8} className="title">
          <h1>Daily Attendance</h1>
          <p>
            {meta.title} on {moment(filterDate || undefined).format("DD MMM YYYY")}
          </p>
        </Col>
        <Col xs={24} md={5}>
          <Radio.Group
            className="attendance-mode"
            value={mode}
            onChange={onModeChange}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value={attendance_type}>Present</Radio.Button>
            <Radio.Button value={absentee_type}>Absent</Radio.Button>
          </Radio.Group>
        </Col>
        <Col xs={24} sm={12} md={4}>
          <DatePicker
            className="full-width"
            value={filterDate}
            allowClear={false}
            disabledDate={(current) => current && current > moment().endOf("day")}
            format="DD/MM/YYYY"
            onChange={(date) => {
              setFilterDate(date || moment());
              setPageNumber(0);
            }}
          />
        </Col>
        <Col xs={24} sm={12} md={4}>
          <Input
            className="report-search"
            placeholder="Search name or card ID"
            value={searchKey}
            onChange={(e) => {
              setSearchKey(e.target.value);
              setPageNumber(0);
            }}
            prefix={<Image className="search-img" name={"search.svg"} />}
            allowClear
          />
        </Col>
        <Col xs={24} md={3}>
          <Tooltip title={total ? "" : "Nothing to export for this selection"}>
            <Button
              block
              loading={exportLoading}
              disabled={!total}
              onClick={onExport}
              type="primary"
              className="csv"
            >
              Download Excel
            </Button>
          </Tooltip>
        </Col>
      </Row>

      <div className="common-table">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.key} className={column.center ? "txt-cente" : undefined}>
                  {column.label}{" "}
                  {sortField === column.key && sortMethod === "asc" ? (
                    <SortAscendingOutlined onClick={() => onSort(column.key)} />
                  ) : (
                    <SortDescendingOutlined onClick={() => onSort(column.key)} />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr className="table-state-row">
                <td colSpan={columns.length} className="table-state">
                  <span className="table-state-spinner" role="status" aria-label="Loading" />
                </td>
              </tr>
            ) : !rows?.length ? (
              <tr className="table-state-row">
                <td colSpan={columns.length} className="table-state">
                  No {meta.title.toLowerCase()} employees on{" "}
                  {moment(filterDate || undefined).format("DD MMM YYYY")}
                  {searchKey ? ` matching “${searchKey}”` : ""}.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={row?.employeeCode || row?.employeeName || index}>
                  {columns.map((column) => (
                    <td key={column.key} className={column.center ? "txt-cente" : undefined}>
                      {renderCell(row, column)}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Row className="table-pagination" align="middle">
        <Col xs={24} md={12}>
          <h5>
            {total
              ? `Showing ${offset + 1} to ${offset + shown} of ${total} entries`
              : "No entries"}
          </h5>
        </Col>
        <Col xs={24} md={12} className="txt-right">
          {total > 0 && (
            <Pagination
              showSizeChanger
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onChange={(page) => setPageNumber(page - 1)}
              onShowSizeChange={(current, size) => {
                setPageSize(size);
                setPageNumber(0);
              }}
              current={pageNumber + 1}
              pageSize={pageSize}
              total={total}
            />
          )}
        </Col>
      </Row>
    </Fragment>
  );
};

export default DailyAttendance;
